(() => {
  'use strict';

  const theme = window.MaisonTheme || {};

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(theme.locale || document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: theme.currency || 'EUR'
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${theme.currency || 'EUR'}`;
    }
  };

  const openMobileMenu = () => {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    const toggle = document.querySelector('[data-menu-toggle]');
    if (!menu || !overlay || !toggle) return;
    menu.inert = false;
    menu.classList.add('is-open');
    overlay.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
    const focusable = menu.querySelector('button, a, summary, input');
    if (focusable) focusable.focus();
  };

  const closeMobileMenu = () => {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    const toggle = document.querySelector('[data-menu-toggle]');
    if (!menu || !overlay || !toggle) return;
    menu.classList.remove('is-open');
    overlay.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    menu.inert = true;
    toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
    toggle.focus();
  };

  const bindMobileMenu = () => {
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      if (button.dataset.menuBound === 'true') return;
      button.dataset.menuBound = 'true';
      button.addEventListener('click', () => {
        const isOpen = document.querySelector('[data-mobile-menu]')?.classList.contains('is-open');
        isOpen ? closeMobileMenu() : openMobileMenu();
      });
    });
    document.querySelectorAll('[data-menu-close], [data-mobile-menu-overlay]').forEach((element) => {
      if (element.dataset.menuBound === 'true') return;
      element.dataset.menuBound = 'true';
      element.addEventListener('click', closeMobileMenu);
    });
  };

  document.addEventListener('keydown', (event) => {
    const menu = document.querySelector('[data-mobile-menu].is-open');
    if (!menu) return;
    if (event.key === 'Escape') closeMobileMenu();
    if (event.key !== 'Tab') return;
    const items = Array.from(menu.querySelectorAll('a[href], button, summary, input, select, [tabindex="0"]'))
      .filter((element) => !element.disabled && element.getClientRects().length);
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });

  const showCartToast = (title) => {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    const titleElement = toast.querySelector('[data-cart-toast-title]');
    if (titleElement) titleElement.textContent = title || theme.strings?.added || 'Ajouté au panier';
    toast.hidden = false;
    window.clearTimeout(showCartToast.timeout);
    showCartToast.timeout = window.setTimeout(() => { toast.hidden = true; }, 5000);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${theme.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    } catch (_error) {
      // The cart page remains the reliable fallback.
    }
  };

  const bindProductForms = (scope = document) => {
    scope.querySelectorAll('form[data-product-form]').forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        const submitter = event.submitter;
        if (submitter && submitter.name && submitter.name !== 'add') return;
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        const button = form.querySelector('[data-add-to-cart]');
        const originalText = button?.querySelector('span')?.textContent || button?.textContent;
        if (button) {
          button.disabled = true;
          button.classList.add('is-loading');
        }
        try {
          const response = await fetch(`${theme.routes?.cartAdd || '/cart/add'}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || theme.strings?.error);
          await updateCartCount();
          showCartToast(data.product_title || data.title || theme.strings?.added);
        } catch (error) {
          window.alert(error.message || theme.strings?.error || 'An error occurred.');
        } finally {
          if (button) {
            button.disabled = false;
            button.classList.remove('is-loading');
            const span = button.querySelector('span');
            if (span && originalText) span.textContent = originalText;
          }
        }
      });
    });
  };

  const bindProductVariants = (scope = document) => {
    scope.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.variantBound === 'true') return;
      section.dataset.variantBound = 'true';
      const jsonElement = section.querySelector('[data-product-variants-json]');
      if (!jsonElement) return;
      let variants = [];
      try { variants = JSON.parse(jsonElement.textContent); } catch (_error) { return; }

      const optionGroups = Array.from(section.querySelectorAll('[data-option-group]'));
      if (!optionGroups.length) return;

      const getSelectedOptions = () => optionGroups.map((group) => {
        const checked = group.querySelector('input:checked');
        const select = group.querySelector('select');
        return checked?.value ?? select?.value ?? '';
      });

      const updateVariant = () => {
        const selected = getSelectedOptions();
        const variant = variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));
        const idInput = section.querySelector('[data-variant-id]');
        const addButton = section.querySelector('[data-add-to-cart]');
        const addButtonText = addButton?.querySelector('span');
        const currentPrice = section.querySelector('[data-price-current]');
        const comparePrice = section.querySelector('[data-price-compare]');
        const sku = section.querySelector('[data-product-sku]');
        const availability = section.querySelector('[data-product-availability]');

        if (!variant) {
          if (idInput) idInput.value = '';
          if (addButton) addButton.disabled = true;
          if (addButtonText) addButtonText.textContent = theme.strings?.unavailable || 'Unavailable';
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
        if (comparePrice) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePrice.textContent = formatMoney(variant.compare_at_price);
            comparePrice.hidden = false;
          } else {
            comparePrice.hidden = true;
          }
        }
        if (sku) sku.textContent = variant.sku || '—';
        if (availability) availability.textContent = variant.available ? (theme.strings?.inStock || 'In stock') : (theme.strings?.outOfStock || theme.strings?.soldOut || 'Out of stock');
        if (addButton) addButton.disabled = !variant.available;
        if (addButtonText) addButtonText.textContent = variant.available ? (theme.strings?.addToCart || 'Add to cart') : (theme.strings?.soldOut || 'Sold out');

        if (variant.featured_media?.id) {
          const media = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (media && window.innerWidth < 750) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      };

      optionGroups.forEach((group) => group.addEventListener('change', updateVariant));
    });
  };

  const bindQuantityControls = (scope = document) => {
    scope.querySelectorAll('[data-quantity-wrapper]').forEach((wrapper) => {
      if (wrapper.dataset.bound === 'true') return;
      wrapper.dataset.bound = 'true';
      const input = wrapper.querySelector('input[type="number"]');
      if (!input) return;
      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        input.value = Math.max(min, Number(input.value || min) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(max, Number(input.value || 0) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const bindLocalizationSelectors = (scope = document) => {
    scope.querySelectorAll('[data-language-selector]').forEach((select) => {
      if (select.dataset.localizationBound === 'true') return;
      select.dataset.localizationBound = 'true';
      select.addEventListener('change', () => {
        if (select.form) select.form.submit();
      });
    });
  };

  const bindCollectionFilters = () => {
    const form = document.querySelector('[data-collection-filters-form]');
    if (form) {
      document.querySelectorAll('[data-filter-input], [data-sort-select]').forEach((input) => {
        if (input.dataset.filterBound === 'true') return;
        input.dataset.filterBound = 'true';
        input.addEventListener('change', () => form.submit());
      });
    }
    const panel = document.querySelector('[data-filter-panel]');
    const overlay = document.querySelector('[data-filter-overlay]');
    document.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        panel?.classList.toggle('is-open');
        overlay?.classList.toggle('is-open');
        document.body.classList.toggle('menu-open');
      });
    });
    overlay?.addEventListener('click', () => {
      panel?.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.classList.remove('menu-open');
    });
  };

  const bindProductRecommendations = (scope = document) => {
    scope.querySelectorAll('product-recommendations[data-url]').forEach((element) => {
      if (element.dataset.loaded === 'true') return;
      element.dataset.loaded = 'true';
      const observer = new IntersectionObserver((entries, currentObserver) => {
        if (!entries[0].isIntersecting) return;
        currentObserver.unobserve(element);
        fetch(element.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const recommendations = html.querySelector('product-recommendations');
            if (recommendations?.innerHTML.trim()) {
              element.innerHTML = recommendations.innerHTML;
              bindProductForms(element);
            }
          })
          .catch(() => {});
      }, { rootMargin: '0px 0px 300px 0px' });
      observer.observe(element);
    });
  };

  const initialize = (scope = document) => {
    bindProductForms(scope);
    bindProductVariants(scope);
    bindQuantityControls(scope);
    bindLocalizationSelectors(scope);
    bindProductRecommendations(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    bindMobileMenu();
    bindCollectionFilters();
    initialize(document);
    document.querySelector('[data-cart-toast-close]')?.addEventListener('click', () => {
      const toast = document.querySelector('[data-cart-toast]');
      if (toast) toast.hidden = true;
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    bindMobileMenu();
    if (!document.querySelector('[data-mobile-menu].is-open')) document.body.classList.remove('menu-open');
  });
  const desktopMenu = window.matchMedia('(min-width: 990px)');
  desktopMenu.addEventListener('change', (event) => {
    if (event.matches && document.querySelector('[data-mobile-menu].is-open')) closeMobileMenu();
  });
})();
